import { Alert as TunaAlert, AlertTitle } from '../primitives/alert.js'
import { Button } from '../primitives/button.js'
import { Snackbar } from '../primitives/snackbar.js'
import { features, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { getPathFromPathOrUrl } from '../navigation/get-path.js'
import { Link } from '../navigation/link.js'
import { useNavigate } from '../navigation/navigate.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import styles from './alert.module.css'

export interface NotificationAlertProps {
  className?: string
  autoHideDuration?: number
}

export const NotificationAlert: FC<NotificationAlertProps> = props => {
  const { autoHideDuration = 10000 } = props
  const dispatch = useThunkDispatch()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const entityId = useSelector(state => state.presence.activeEntityId)
  const alert = useSelector(state => state.notifications.alert)
  const ancestors = graphHooks.useAncestors({ parentId: entityId ?? undefined })

  useEffect(() => {
    if (!alert) {
      return
    }
    setOpen(true)
  }, [alert, ancestors, entityId])

  const handleClose = useCallback(() => {
    setOpen(false)
    dispatch(features.notifications.actions.setAlert(null))
  }, [dispatch])

  const nav = useCallback(
    (href: string) => () => {
      void navigate(getPathFromPathOrUrl(href))
      setOpen(false)
    },
    [navigate]
  )

  if (!alert) {
    return null
  }

  const { severity, message, title, duration = autoHideDuration, actions, link } = alert

  const actionButtons =
    actions?.map(({ title, action }, idx) => (
      <Button key={idx} onClick={nav(action)}>
        {title}
      </Button>
    )) ?? []
  const action = <>{actionButtons}</>

  return (
    <Snackbar open={open} autoHideDuration={duration} onClick={handleClose} onClose={handleClose}>
      <TunaAlert className={styles.toast} data-severity={severity ?? 'success'} severity={severity} action={action}>
        {title ? <AlertTitle>{title}</AlertTitle> : null}
        {link ? <Link to={link}>{message}</Link> : message}
      </TunaAlert>
    </Snackbar>
  )
}

export default NotificationAlert
